/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { containerService } from '../services/api';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

export interface Container {
  id: string;
  name: string;
  image: string;
  status: string;
  cpu_cores: number;
  memory_gb: number;
  gpu_count?: number;
  gpu_type?: string;
  replicas?: number;
  created_at: string;
  project_id: string;
}

// GPU unavailable error response from backend
export interface GPUUnavailableError {
  error: string;
  code: string;
  gpu_type: string;
  required: number;
  can_join_waitlist: boolean;
  waitlist_info: {
    endpoint: string;
    container_id: string;
    cluster_id: string;
    gpu_type: string;
    gpu_cores: number;
    gpu_memory_gb: number;
    cpu_cores: number;
    memory_gb: number;
    storage_gb: number;
  };
  message: string;
}

export interface UseContainersOptions {
  onGPUUnavailable?: (error: GPUUnavailableError, containerId: string) => void;
}

export const useContainers = (projectId?: string, options?: UseContainersOptions) => {
  const queryClient = useQueryClient();

  const { data: containersData, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: ['containers', projectId],
    queryFn: () => containerService.list(projectId).then((res) => res.data),
    enabled: !!projectId,
  });

  // Handle various response formats - API may return array directly or wrapped in object
  const containers: Container[] = Array.isArray(containersData)
    ? containersData
    : containersData?.containers || containersData?.data || [];
  const runningContainers = containers.filter((c) => c.status?.toLowerCase() === 'running');

  const startMutation = useMutation({
    mutationFn: (id: string) => containerService.start(id, projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers', projectId] });
      toast.success('Container started successfully');
    },
    onError: (error: AxiosError<GPUUnavailableError>, containerId: string) => {
      // Check if this is a GPU unavailable error
      if (
        error.response?.status === 409 &&
        error.response?.data?.code === 'GPU_UNAVAILABLE' &&
        error.response?.data?.can_join_waitlist
      ) {
        // Call the callback if provided
        if (options?.onGPUUnavailable) {
          options.onGPUUnavailable(error.response.data, containerId);
          return;
        }
      }
      // Default error handling
      toast.error(error.response?.data?.error || 'Failed to start container');
    },
  });

  const stopMutation = useMutation({
    mutationFn: (id: string) => containerService.stop(id, projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers', projectId] });
      toast.success('Container stopped successfully');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to stop container');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => containerService.delete(id, projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers', projectId] });
      toast.success('Container deleted successfully');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to delete container');
    },
  });

  const scaleMutation = useMutation({
    mutationFn: ({ id, replicas }: { id: string; replicas: number }) =>
      containerService.scale(id, projectId!, replicas),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers', projectId] });
      toast.success('Container scaled successfully');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to scale container');
    },
  });

  return {
    containers,
    runningContainers,
    runningCount: runningContainers.length,
    totalCount: containers.length,
    isLoading: isLoading || isPending,
    isError,
    refetch,
    startContainer: startMutation.mutate,
    isStarting: startMutation.isPending,
    stopContainer: stopMutation.mutate,
    isStopping: stopMutation.isPending,
    deleteContainer: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    scaleContainer: scaleMutation.mutate,
    isScaling: scaleMutation.isPending,
  };
};
